// Small pieces the campaign screens share. Nothing here fetches.

import type { ReactNode } from "react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CircleAlertIcon,
  CircleCheckIcon,
  CircleDashedIcon,
  CircleXIcon,
} from "lucide-react";
import type { CampaignState } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { statusLook } from "@/components/emc/status";
import { Flag } from "@/components/emc/Flag";
import { cn } from "@/lib/utils";
import type { Check, CheckStatus } from "@/pages/campaigns/checks";
import type { StepKey } from "@/pages/campaigns/model";

/** Country codes with their flags, each country's state on hover. Only a
 * failed country is in the signal colour — it is the one somebody has to act
 * on (restart it, or give up); a stopped country was somebody's decision, and
 * is shown quieter rather than louder. */
export function CountryCodes({
  countries,
  className,
}: {
  countries: { country: string; state?: CampaignState }[];
  className?: string;
}) {
  if (countries.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <span className={cn("inline-flex flex-wrap gap-x-2.5 gap-y-1", className)}>
      {countries.map(({ country, state }) => {
        const look = state === undefined ? null : statusLook("campaign", state);
        return (
          <span
            key={country}
            title={look === null ? undefined : `${country}: ${look.label}`}
            className={cn(
              "inline-flex items-center gap-1.5 font-semibold",
              state === "failed" && "text-red-ink",
              state === "cancelled" && "text-muted-foreground",
            )}
          >
            <Flag code={country} className={cn(state === "cancelled" && "opacity-50")} />
            {country}
          </span>
        );
      })}
    </span>
  );
}

const CHECK_ICON: Record<CheckStatus, { icon: typeof CircleCheckIcon; className: string; word: string }> = {
  ok: { icon: CircleCheckIcon, className: "text-success", word: "rendben" },
  fail: { icon: CircleXIcon, className: "text-destructive", word: "hiányzik" },
  warn: { icon: CircleAlertIcon, className: "text-warning", word: "figyelmeztetés" },
  pending: { icon: CircleDashedIcon, className: "text-muted-foreground", word: "ellenőrzés alatt" },
};

/** The review checklist: each item's statement, its answer, and a way to the
 * step that fixes it. */
export function CheckList({
  checks,
  onGo,
}: {
  checks: Check[];
  onGo?: (step: StepKey) => void;
}) {
  return (
    <ul className="m-0 flex list-none flex-col p-0" aria-label="Ellenőrzőlista">
      {checks.map((check) => {
        const look = CHECK_ICON[check.status];
        const Icon = look.icon;
        return (
          <li
            key={check.key}
            className="flex items-start gap-3 border-b border-separator py-2.5 last:border-b-0"
            data-check={check.key}
            data-status={check.status}
          >
            <Icon className={cn("mt-0.5 size-4 shrink-0", look.className)} aria-hidden="true" />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="font-medium">
                {check.label}
                <span className="sr-only"> — {look.word}</span>
              </span>
              <span
                className={cn(
                  "break-words",
                  check.status === "fail" ? "text-ink" : "text-muted-foreground",
                )}
              >
                {check.detail}
              </span>
            </div>
            {onGo && check.step && check.status !== "ok" && check.status !== "pending" && (
              <Button variant="link" type="button" onClick={() => onGo(check.step!)} className="shrink-0">
                {/* A warning is read, not fixed: nothing there is wrong. */}
                {check.status === "fail" ? "Javítás" : "Megnézem"}
              </Button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** The foot of a step: back on the left, the step's forward move on the
 * right, and — when it cannot be pressed — the sentence that says why. */
export function StepActions({
  onBack,
  backLabel = "Vissza",
  next,
}: {
  onBack?: () => void;
  backLabel?: string;
  next?: {
    label: ReactNode;
    onClick: () => void;
    disabled?: boolean;
    /** Said under the button while it is disabled. */
    reason?: string | null;
    busy?: boolean;
  };
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-t border-separator pt-4">
      {onBack ? (
        <Button variant="outline" type="button" onClick={onBack}>
          <ArrowLeftIcon />
          {backLabel}
        </Button>
      ) : (
        <span />
      )}
      {next && (
        <div className="flex flex-col items-end gap-1.5">
          <Button type="button" onClick={next.onClick} disabled={next.disabled || next.busy}>
            {next.busy ? "Mentés…" : next.label}
            {!next.busy && <ArrowRightIcon />}
          </Button>
          {next.disabled && next.reason && (
            <p className="max-w-prose text-right text-label text-muted-foreground">{next.reason}</p>
          )}
        </div>
      )}
    </div>
  );
}
